import semver from 'semver';

/**
 * WHEN THE UPDATE DIALOG IS ALLOWED TO ANNOUNCE ITSELF.
 *
 * One pure function, one place. Pure means pure: no `new Date()`, no store
 * reads, no Tauri imports (`40-conventions.md` — shared app code never
 * statically imports `@tauri-apps/api/*`; this module imports `semver` and
 * nothing else). `today` arrives as a required string so every daily-boundary
 * case is testable as string input — no fake timers, no timezone dependence,
 * and no suite that straddles midnight.
 *
 * ── Three gestures, three meanings ───────────────────────────────────────
 *
 *   Later            writes nothing. Modal closes, badge STAYS LIT, and the
 *                    dialog asks again tomorrow.
 *   Skip this version writes `skippedUpdateVersion`. Badge goes DARK for that
 *                    exact version, and only that version.
 *   Close (X/Esc/backdrop) writes NOTHING AT ALL. Same as Later.
 *
 * `updatePromptLastShownDay` is written when the modal is AUTO-ANNOUNCED, not
 * by any dismissal handler. That is the whole trick: it is what lets closing
 * the dialog keep writing nothing while still not re-nagging the same day.
 *
 * ── Why closing must never dismiss (history — do not delete) ─────────────
 *
 * `onUpdateModalClose` used to call `dismissUpdate`, which silenced that
 * version for good: the startup prompt never returned AND the indicator went
 * dark with it, because the same dismissed-version check feeds both
 * (`updateUtils.js` `isUpdateAvailable`). Closing a dialog is the most
 * reflexive thing anyone does with one; that it permanently opted you out of
 * an update you had not decided about is the whole reason updates appeared to
 * only exist inside Settings. That was shipped, was wrong, and was reverted.
 * It stays reverted. The daily cap lives HERE and only here, deliberately, so
 * that it cannot reach `isUpdateAvailable` and therefore cannot reach the
 * badge.
 *
 * ── Why the badge is the way back ────────────────────────────────────────
 *
 * "Later" means "not now". Clearing `updateAvailable` made it mean "never",
 * after which the only route to an available update was Settings. The badge is
 * how somebody changes their mind, which is the entire point of a badge — so
 * rule 3 below returns `badge: true`. Only an explicit, informed per-version
 * refusal (Skip) darkens it.
 *
 * ── Rule 2 is `semver.eq`. DO NOT WIDEN IT TO `lte`/`gte`. ───────────────
 *
 * A newer offer simply stops matching, so prompt and badge both return on their
 * own. The skip record is never cleared and never overwritten by a newer
 * version: a manifest that flaps between two versions must not resurrect a
 * prompt for the version the user already refused. Only pressing Skip on a
 * different version overwrites it.
 *
 * Exact match is also the pre-existing shipped semantics — `isUpdateAvailable`
 * (`updateUtils.js`) has compared the dismissal with `semver.eq` all along — so
 * `eq` preserves behaviour rather than quietly widening it, which is the right
 * default for a change nobody asked for. And it is the better meaning on its
 * own merits: "Skip this version" is a statement about THAT version, not a
 * floor. Under `lte`, a user who skipped 1.0.5 and is then offered 1.0.3 (a
 * carried-forward platform entry, or a manifest rolled back) would be silenced
 * about a version they never skipped. `isUpdateAvailable` already requires
 * `offered > currentVersion`, so that is the only reachable gap — and prompting
 * there is correct. Settled by the coordinator, 2026-08-26.
 *
 * @param {{
 *   updateAvailable: boolean,
 *   offeredVersion: string,       // resolveArtifactVersion(), never the manifest headline
 *   skippedVersion: string|null,
 *   lastPromptedDay: string|null, // "YYYY-MM-DD" UTC
 *   today: string                 // REQUIRED. utcDayStamp() at the call site.
 * }} input
 * @returns {{ action: 'none'|'prompt', badge: boolean, reason: string }}
 */
export function decideUpdatePrompt(input) {
  const {
    updateAvailable,
    offeredVersion,
    skippedVersion,
    lastPromptedDay,
    today,
  } = input || {};

  if (!updateAvailable || !semver.valid(offeredVersion)) {
    return { action: 'none', badge: false, reason: 'no-update' };
  }

  if (semver.valid(skippedVersion) && semver.eq(offeredVersion, skippedVersion)) {
    return { action: 'none', badge: false, reason: 'skipped' };
  }

  // String equality ONLY — never `<`/`>`. A stamp in the future (clock skew, a
  // restored backup, a machine whose date was wrong for an afternoon) must let
  // the app prompt again, not silence it until the calendar catches up.
  if (lastPromptedDay && lastPromptedDay === today) {
    return { action: 'none', badge: true, reason: 'daily-cap' };
  }

  return { action: 'prompt', badge: true, reason: 'prompt' };
}
